// src/theme/CustomThemeProvider.js
import React, { createContext, useContext, useMemo, useState } from "react";
import { ThemeProvider, createTheme, CssBaseline, IconButton, Tooltip } from "@mui/material";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";

const ColorModeContext = createContext();

export const useColorMode = () => useContext(ColorModeContext);

export function CustomThemeProvider({ children }) {
  const [mode, setMode] = useState(localStorage.getItem("theme") || "light");

  const toggleColorMode = () => {
    setMode((prev) => {
      const next = prev === "light" ? "dark" : "light";
      localStorage.setItem("theme", next);
      return next;
    });
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: "#1976d2" },
          background: {
            default: mode === "dark" ? "#181c21" : "#f3f6fa",
            paper: mode === "dark" ? "#23272e" : "#fff",
          },
          text: {
            primary: mode === "dark" ? "#fff" : "#222",
            secondary: mode === "dark" ? "#aaa" : "#666"
          }
        },
        shape: { borderRadius: 14 },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundColor: mode === "dark" ? "#23272e" : "#fff",
                color: mode === "dark" ? "#fff" : "#222"
              }
            }
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundColor: mode === "dark" ? "#23272e" : "#fff",
                color: mode === "dark" ? "#fff" : "#222"
              }
            }
          }
        }
      }),
    [mode]
  );


  return (
    <ColorModeContext.Provider value={{ mode, toggleColorMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export function ThemeToggleButton({ sx }) {
  const { mode, toggleColorMode } = useColorMode();
  return (
    <Tooltip title={mode === "dark" ? "Light mode" : "Dark mode"}>
      <IconButton
        onClick={toggleColorMode}
        color="inherit"
        sx={sx}
        size="large"
        aria-label="toggle dark mode"
      >
        {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
      </IconButton>
    </Tooltip>
  );
}
